import React, { useCallback, useEffect, useRef, useState } from "react";
import "../css/notificationPage.css";
import {
  IconSettingNoti,
  NewsletterIcon,
  SidebarCareer,
  SidebarContent,
  SidebarInbox,
  SidebarMedia,
  SidebarOverview,
  SidebarWorkflow,
} from "../../../../asset-controller-mod/asset-serv/icons/iconExporter";
import { useNavigate } from "react-router-dom";
import { groupPermissionAccessCMSNotification } from "../../../../context-controller-mod/context-serv/permission-provider/permissionExporter";
// Add to imports
import { getAllNotification, getNotificationRoute, NotificationApiItem, NotificationMessage, readNotification, subscribeNotificationSSE } from './notificationType.type';
import EmptyNotification from "../../../../component-controller-mod/component-serv/dashboard-fs-cp/notification-dsb-op/empty-notification/ts/emptyNotification";

type AvatarColor = "green" | "blue" | "red";

export interface NotificationCardItem {
  id: string;
  uuid: string;
  module: string;
  avatarColor: AvatarColor;
  avatarIcon: React.ReactNode;
  title: string;
  body: string;
  time: string;
  readStatus: string;
}

const TABS = ["All", "Unread"];
const PAGE_SIZE = 10;

const Avatar = ({
  color,
  icon,
}: {
  color: AvatarColor;
  icon: React.ReactNode;
}) => (
  <div
    className={`scl--cms-pages-notificationPage-avatar scl--cms-pages-notificationPage-avatar--${color}`}
  >
    {icon}
  </div>
);

const getNotificationIcon = (module: string): React.ReactNode => {
  switch (module.trim().toLowerCase()) {
    case "workflow":
      return <SidebarWorkflow />;
    case "content":
      return <SidebarContent />;
    case "career":
      return <SidebarCareer />;
    case "setting":
      return <IconSettingNoti />;
    case "inbox":
      return <SidebarInbox />;
    case "media":
      return <SidebarMedia />;
    case "oerview":
      return <SidebarOverview />;
    default:
      return <NewsletterIcon />;
  }
};

const NotificationRow = React.forwardRef<
  HTMLDivElement,
  {
    item: NotificationCardItem;
    onClick?: () => void;
  }
>(({ item, onClick }, ref) => (
  <div //NOSONAR
    className="scl--cms-pages-notificationPage-row"
    ref={ref}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick?.();
      }
    }}
  >
    <Avatar color={item.avatarColor} icon={item.avatarIcon} />
    <div className="scl--cms-pages-notificationPage-rowContent">
      <div className="scl--cms-pages-notificationPage-rowText">
        <span className="scl--cms-pages-notificationPage-rowTitle" //NOSONAR
          onClick={onClick}
        >
          {item.title}
        </span>
        <span className="scl--cms-pages-notificationPage-rowBody">
          {item.body}
        </span>
      </div>
      <span className="scl--cms-pages-notificationPage-rowTime">
        {item.time}
      </span>
    </div>
  </div>
));

NotificationRow.displayName = "NotificationRow";

const formatNotificationTime = (isoDate: string): string => {
  const created = new Date(isoDate).getTime();

  if (Number.isNaN(created)) return "Unknown time";

  const now = Date.now();
  const diffMs = Math.max(0, now - created);

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const NotificationPage = () => {
  const [activeTab, setActiveTab] = useState<string>("All");
  const [notifications, setNotifications] = useState<NotificationCardItem[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate()

  const observerRef = useRef<IntersectionObserver | null>(null);

  const canAccessNoti = groupPermissionAccessCMSNotification.view()

  useEffect(() => {
    if(!canAccessNoti){
      navigate("forbidden")
    }
  },[])

  const parseMessage = (raw: string): NotificationMessage => {
    try {
      return JSON.parse(raw);
    } catch {
      return {
        module: "",
        message: raw,
      };
    }
  };

 const mapNotification = (item: NotificationApiItem): NotificationCardItem => {
  // message may already be a parsed object from SSE getApiRealtime
  const parsed: NotificationMessage =
    typeof item.message === 'object' && item.message !== null
      ? item.message as NotificationMessage//NOSONAR
      : parseMessage(String(item.message ?? ''));

  return {
    id:          String(item.id),
    uuid:        item.uuid,
    module:      parsed.module  ?? '',
    avatarColor: item.readStatus === 'UNREAD' ? 'blue' : 'green',
    avatarIcon:  getNotificationIcon(parsed.module ?? ''),
      title:       parsed.module  ?? 'Notification',
      body:        parsed.message ?? 'No message',
      time:        formatNotificationTime(item.createdAt),
      readStatus:  item.readStatus,
    };
  };

  const fetchNotifications = useCallback(
    async (pageNumber: number, isLoadMore = false) => {
      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        setError("");

        const res = await getAllNotification(pageNumber, PAGE_SIZE);

        const items = Array.isArray(res.data.data)
          ? res.data.data
          : res.data.data?.items ?? [];

        const mappedNotifications = items.map(mapNotification);

        setNotifications((prev) =>
          isLoadMore ? [...prev, ...mappedNotifications] : mappedNotifications
        );

        setHasMore(items.length === PAGE_SIZE);
      } catch (err: any) {
        console.log("notification error:", err);
        setError(err?.message ?? "Failed to load notifications");

        if (!isLoadMore) {
          setNotifications([]);
        }
      } finally {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  useEffect(() => {
    if (page === 1) return;
    fetchNotifications(page, true);
  }, [page, fetchNotifications]);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || loadingMore || !hasMore) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      });

      if (node) {
        observerRef.current.observe(node);
      }
    },
    [loading, loadingMore, hasMore]
  );

  const filteredNotifications =
    activeTab === "Unread"
      ? notifications.filter((item) => item.readStatus === "UNREAD")
      : notifications;

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  useEffect(() => {
    const unsubscribe = subscribeNotificationSSE((item: NotificationApiItem) => {
      const mapped = mapNotification(item);
      setNotifications((prev) => {
        // Avoid duplicates
        if (prev.some((n) => n.id === mapped.id)) return prev;//NOSONAR
        // Prepend newest item
        return [mapped, ...prev];
      });
    });
    return unsubscribe;
  }, []);

  const handleClickItemNotify = async (item: NotificationCardItem) => {
    try {
      if (item.readStatus === "UNREAD") {
        const res = await readNotification(item.uuid);

        if (res?.data?.success) {
          setNotifications((prev) =>
            prev.map((notification) =>
              notification.uuid === item.uuid
                ? {
                  ...notification,
                  readStatus: "READ",
                  avatarColor: "green",
                }
                : notification
            )
          );
        }
      }

      const targetRoute = getNotificationRoute(item.module);

      if (targetRoute) {
        navigate(targetRoute);
      }
    } catch (err) {
      console.log("read notification error:", err);
    }
  };

  return (
    <div className="scl--cms-pages-notificationPage">
      <section className="notification-section">
        <h1 className="scl--cms-pages-notificationPage-title">Notification</h1>
        <p className="scl--cms-pages-notificationPage-description">
          Controls how content is created, reviewed, published, and managed to
          maintain quality and consistency.
        </p>

        <div className="scl--cms-pages-notificationPage-tabBar">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className="scl--cms-pages-notificationPage-tabBtn"
              onClick={() => handleTabChange(tab)}
            >
              <span
                className={`scl--cms-pages-notificationPage-tabLabel ${activeTab === tab
                  ? "scl--cms-pages-notificationPage-tabLabel--active"
                  : "scl--cms-pages-notificationPage-tabLabel--inactive"
                  }`}
              >
                {tab}
              </span>

              {activeTab === tab && (
                <div className="scl--cms-pages-notificationPage-tabIndicator" />
              )}
            </button>
          ))}
        </div>
      </section>


      <div
        ref={contentRef}
        className="scl--cms-pages-notificationPage-content"
      >
        {loading && <p>Loading...</p>}
        {error && <p>{error}</p>}
        {!loading && !error && filteredNotifications.length === 0 && (
          <EmptyNotification />
        )}

        {!loading &&
          !error &&
          filteredNotifications.map((item, index) => {
            const isLast = index === filteredNotifications.length - 1;

            return (
              <NotificationRow
                key={item.id}
                item={item}
                ref={isLast ? lastItemRef : null}
                onClick={() => handleClickItemNotify(item)}
              />
            );
          })}

        {loadingMore && <p>Loading more...</p>}
        {!hasMore && !loading && !loadingMore && notifications.length > 0 && (
          <p>No more notifications</p>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;